// frontend/src/services/socketService.js
let io = null;
let socket = null;
let reconnectAttempts = 0;
const maxReconnectAttempts = 5;

const initializeSocket = async () => {
  try {
    if (socket && socket.connected) return socket;

    const socketModule = await import('socket.io-client');
    io = socketModule.default || socketModule.io;

    // Connect to worker WebSocket (port 5002) for real-time render progress
    // In production, use the same hostname via nginx /socket.io/ proxy
    // Fallback: connect directly to worker port 5002
    const socketUrl = process.env.REACT_APP_SOCKET_URL ||
      `${window.location.protocol}//${window.location.hostname}:5002`;
    console.log('🔌 Connecting to worker socket server:', socketUrl);

    socket = io(socketUrl, {
      timeout: 15000,
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: maxReconnectAttempts,
      reconnectionDelay: 2000,
      transports: ['websocket', 'polling'],
      withCredentials: false
    });

    socket.on('connect', () => {
      console.log('✅ Socket connected successfully:', socket.id);
      reconnectAttempts = 0;
    });

    socket.on('disconnect', (reason) => {
      console.log('🔌 Socket disconnected:', reason);
    });

    socket.on('connect_error', (error) => {
      console.warn('⚠️ Socket connection failed:', error.message);
      reconnectAttempts++;
    });

    return socket;
  } catch (error) {
    console.warn('⚠️ Socket.io not available:', error.message);
    return null;
  }
};

const ensureSocket = async () => {
  if (!socket || !socket.connected) {
    await initializeSocket();
  }
  return socket;
};

const addSocketListener = (event, callback) => {
  if (!socket) return () => {};
  
  const handler = (data) => {
    console.log(`📡 Socket event: ${event}`, data);
    callback(data);
  };
  
  socket.on(event, handler);
  return () => socket.off(event, handler);
};

export const subscribeToJobUpdates = (jobId, callback) => {
  if (!jobId) return () => {};

  let active = true;
  let cleanup = () => {};

  ensureSocket().then((socket) => {
    if (!socket || !active) return;

    console.log(`🔄 Subscribing to job updates: ${jobId}`);
    socket.emit('job:subscribe', jobId);

    const cleanup1 = addSocketListener('job:update', (data) => {
      if (active && (data.jobId === jobId || data._id === jobId)) {
        callback(data);
      }
    });

    const cleanup2 = addSocketListener('user:job:update', (data) => {
      if (active && (data.jobId === jobId || data._id === jobId)) {
        callback(data);
      }
    });

    // Worker emits 'job:progress' for render progress updates
    const cleanup3 = addSocketListener('job:progress', (data) => {
      if (active && (data.jobId === jobId || data._id === jobId)) {
        callback(data);
      }
    });

    cleanup = () => {
      cleanup1();
      cleanup2();
      cleanup3();
    };
  });

  return () => {
    active = false;
    cleanup();
  };
};

export const subscribeToUserJobUpdates = (userId, callback) => {
  if (!userId) return () => {};

  let active = true;
  let cleanup = () => {};

  ensureSocket().then((socket) => {
    if (!socket || !active) return;

    console.log(`🔄 Subscribing to user job updates: ${userId}`);
    
    // Join user room
    socket.emit('joinUserRoom', userId);

    cleanup = addSocketListener('user:job:update', (data) => {
      if (active) {
        console.log('📡 User job update received:', data);
        callback(data);
      }
    });
  });

  return () => {
    active = false;
    cleanup();
  };
};

// services/socketService.js
export const subscribeToQueueUpdates = (adminToken, callback) => {
  let active = true;
  let cleanup = () => {};

  ensureSocket().then((socket) => {
    if (!socket || !active) return;

    console.log('🔄 Subscribing to admin queue updates');
    
    // ✅ Join admin room with token
    socket.emit('admin:join', { token: adminToken });

    cleanup = addSocketListener('queue:update', (data) => {
      if (active) {
        console.log(`📊 Queue update received:`, data);
        callback(data);
      }
    });
  }).catch(error => {
    console.error('Failed to subscribe to queue updates:', error);
  });

  return () => {
    active = false;
    cleanup();
  };
};

export const disconnectSocket = () => {
  if (socket) {
    console.log('🔌 Disconnecting socket');
    socket.disconnect();
    socket = null;
  }
};

export { ensureSocket };